import { useMemo, useState, useEffect } from "react";
import { useAtom } from "jotai";
import { sidebarOpenAtom } from "@/features/Navigation/state";
import { useMediaQuery } from "@/ui/hooks/useMediaQuery";
import useNavigation from "@/features/Navigation/hooks/useNavigation";
import SearchBar from "@/features/Navigation/components/SearchBar";
import { NavigationNode } from "./NavigationNode";
import { useFlags } from "@/utils/flagsmith/useFlags";
import clsx from "clsx";

export type { NavigationNode } from "./NavigationNode";

export default function NavigationTree() {
    const flags = useFlags();
    const showComingSoon = flags.enabled("show-coming-soon-placeholders");
    const showProjectionMetrics = flags.enabled("show-projection-metrics");

    const [, setSidebarOpen] = useAtom(sidebarOpenAtom);
    const isMobile = useMediaQuery("(max-width: 1024px)");
    const [searchTerm, setSearchTerm] = useState("");
    const [activeItemId, setActiveItemId] = useState<string>();
    const [openIds, setOpenIds] = useState<string[]>(["executive-summary"]);

    const { handleNavigation } = useNavigation({
        onNavigate: (item) => {
            setActiveItemId(item.id);
            if (isMobile) {
                setSidebarOpen(false);
            }
        },
        scrollBehavior: "smooth",
    });

    const nodes: NavigationNode[] = useMemo(
        () => [
            {
                id: "executive-summary",
                name: "Executive Summary",
                nodes: [
                    {
                        id: "company-overview",
                        name: "Company Overview",
                        anchor: "company-overview",
                    },
                    {
                        id: "transaction-summary",
                        name: "Transaction Summary",
                        anchor: "transaction-summary",
                    },
                    {
                        id: "deal-structure",
                        name: "Deal Structure",
                        anchor: "deal-structure",
                    },
                    {
                        id: "financial-highlights",
                        name: "Financial Highlights",
                        anchor: "financial-highlights",
                    },
                    {
                        id: "investment-rationale",
                        name: "Investment Rationale",
                        anchor: "investment-rationale",
                    },
                    {
                        id: "due-diligence",
                        name: "Due Diligence",
                        anchor: "due-diligence",
                    },
                    ...(showComingSoon
                        ? [
                              {
                                  id: "key-risks",
                                  name: "Key Risks",
                                  anchor: "key-risks",
                              },
                              {
                                  id: "funding-and-valuation",
                                  name: "Funding & Valuation",
                                  anchor: "funding-and-valuation",
                              },
                          ]
                        : []),
                    {
                        id: "industry-overview",
                        name: "Industry & Value Chain Analysis",
                        anchor: "industry-overview",
                    },
                ],
            },
            {
                id: "financials-projections",
                name: "Financials & Projections",
                nodes: [
                    {
                        id: "summary-financials",
                        name: "Summary Financials",
                        anchor: "summary-financials",
                    },
                    ...(showProjectionMetrics
                        ? [
                              {
                                  id: "summary-projections",
                                  name: "Summary Projections",
                                  anchor: "summary-projections",
                              },
                          ]
                        : []),
                ],
            },
        ],
        [showComingSoon, showProjectionMetrics],
    );

    const filteredNodes = useMemo(() => {
        if (!searchTerm.trim()) return nodes;

        const filterNodes = (nodes: NavigationNode[]): NavigationNode[] => {
            return nodes
                .map((node) => {
                    const childMatches = node.nodes
                        ? filterNodes(node.nodes)
                        : [];

                    const nameMatches = node.name
                        .toLowerCase()
                        .includes(searchTerm.toLowerCase());

                    if (nameMatches || childMatches.length > 0) {
                        return {
                            ...node,
                            ...(childMatches.length > 0
                                ? { nodes: childMatches }
                                : {}),
                        };
                    }

                    return null;
                })
                .filter((node): node is NavigationNode => node !== null);
        };

        return filterNodes(nodes);
    }, [nodes, searchTerm]);

    // Auto-expand all when searching
    useEffect(() => {
        if (searchTerm.trim()) {
            const getAllIds = (nodes: NavigationNode[]): string[] => {
                return nodes.flatMap((node) => [
                    node.id,
                    ...(node.nodes ? getAllIds(node.nodes) : []),
                ]);
            };
            setOpenIds(getAllIds(filteredNodes));
        }
    }, [searchTerm, filteredNodes]);

    const handleToggle = (nodeId: string, open: boolean) => {
        setOpenIds((prev) =>
            open ? [...prev, nodeId] : prev.filter((id) => id !== nodeId),
        );
    };

    const handleNodeClick = (node: NavigationNode) => {
        if (node.anchor) {
            handleNavigation({
                id: node.id,
                anchor: node.anchor,
            });
        }
    };

    return (
        <div className={clsx("flex flex-1 flex-col", "min-h-0 gap-3")}>
            <div className="sm:px-3">
                <SearchBar value={searchTerm} onChange={setSearchTerm} />
            </div>
            <div
                className={clsx(
                    "flex min-h-0 flex-1 flex-col gap-2",
                    "overflow-y-auto overflow-x-hidden",
                    "sm:px-3",
                )}
            >
                {filteredNodes.length === 0 && searchTerm.trim() ? (
                    <div
                        className={clsx(
                            "px-2 py-4",
                            "subdued body-s text-center",
                        )}
                    >
                        No results found for &quot;{searchTerm}&quot;
                    </div>
                ) : (
                    <ul className="flex flex-col gap-2">
                        {filteredNodes.map((node) => (
                            <NavigationNode
                                node={node}
                                key={node.id}
                                openIds={openIds}
                                setIsOpen={(open) =>
                                    handleToggle(node.id, open)
                                }
                                level={0}
                                onNavigate={handleNodeClick}
                                activeId={activeItemId}
                            />
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
